import { useEffect, useRef, useState } from 'react';
import type {
  AccionPropuesta,
  CrmOpportunity,
  ImagenAsistente,
  PropuestaAsistente,
  TurnoAsistente,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { useDictado } from '../dictado.js';
import { Icono } from './Iconos.js';

/**
 * EL ASISTENTE DEL CRM, EN PANTALLA.
 *
 * Se escribe como se escribía en el chat de ChatGPT —«hablé con Pouline, está
 * analizando nuestra oferta»— o se pega la captura de la conversación. Lo que
 * vuelve es una propuesta por oportunidad: la nota, la columna, el siguiente
 * paso. Se lee, se corrige si hace falta y se aplica con un toque. Si no está
 * claro de quién se habla, pregunta, y se le contesta pulsando una opción.
 *
 * VIVE AL LADO DEL TABLERO, NO ENCIMA. Es un panel que se abre y se cierra sin
 * desmontarse: cerrarlo para mirar una tarjeta y volver no puede costar la
 * conversación, ni la propuesta que estaba a medio revisar.
 */

/** Una captura ya reducida, lista para enviar y para enseñar en miniatura. */
interface Captura {
  id: string;
  imagen: ImagenAsistente;
  url: string;
}

/** Lo que la persona ha decidido sobre cada acción antes de aplicar. */
interface Decision {
  incluir: boolean;
  nota: string;
  mover: boolean;
  tarea: boolean;
}

type Turno =
  | { id: string; rol: 'PERSONA'; texto: string; capturas: Captura[] }
  | {
      id: string;
      rol: 'ASISTENTE';
      propuesta: PropuestaAsistente;
      decisiones: Decision[];
      estado: 'PENDIENTE' | 'APLICADA' | 'DESCARTADA';
    }
  | { id: string; rol: 'AVISO'; texto: string; error?: boolean };

const MAX_CAPTURAS = 6;
/**
 * El lado largo de una captura, en píxeles. El modelo no lee mejor por encima
 * de esto —reduce él mismo lo que le llega—, y una captura de móvil a tamaño
 * natural pesa cinco veces más para decir lo mismo.
 */
const LADO_MAXIMO = 1600;

let siguiente = 0;
const nuevoId = () => `t${Date.now()}-${(siguiente += 1)}`;

export function AsistenteCrm({
  idioma,
  abierto,
  onCerrar,
  onAplicado,
}: {
  idioma: Idioma;
  abierto: boolean;
  onCerrar: () => void;
  /** Las oportunidades ya cambiadas: el tablero las sustituye sin recargar. */
  onAplicado: (oportunidades: CrmOpportunity[]) => void;
}) {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [texto, setTexto] = useState('');
  const [capturas, setCapturas] = useState<Captura[]>([]);
  const [pensando, setPensando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [aplicando, setAplicando] = useState(false);
  const dictado = useDictado(idioma, setTexto);
  const fichero = useRef<HTMLInputElement>(null);
  const fondo = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLTextAreaElement>(null);

  // El reloj mientras piensa: con capturas tarda, y un spinner mudo durante
  // cuarenta segundos parece colgado.
  useEffect(() => {
    if (!pensando) return;
    setSegundos(0);
    const reloj = window.setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => window.clearInterval(reloj);
  }, [pensando]);

  // Lo último, a la vista.
  useEffect(() => {
    fondo.current?.scrollIntoView({ block: 'end' });
  }, [turnos, pensando]);

  useEffect(() => {
    if (abierto) campo.current?.focus();
  }, [abierto]);

  const hiloActual = (): Turno[] => turnos.slice(inicioDelHilo(turnos, turnos.length));

  async function anadirFicheros(lista: FileList | File[]) {
    const imagenes = [...lista].filter((f) => f.type.startsWith('image/'));
    const hueco = MAX_CAPTURAS - capturas.length;
    if (imagenes.length > hueco) {
      setTurnos((p) => [...p, { id: nuevoId(), rol: 'AVISO', texto: t(idioma, 'crm.asistente.demasiadas') }]);
    }
    const nuevas: Captura[] = [];
    for (const f of imagenes.slice(0, Math.max(0, hueco))) {
      try {
        nuevas.push(await reducir(f));
      } catch {
        // Una imagen que el navegador no sabe abrir se ignora: las demás siguen.
      }
    }
    if (nuevas.length > 0) setCapturas((p) => [...p, ...nuevas]);
  }

  async function enviar(textoAEnviar: string = texto) {
    const limpio = textoAEnviar.trim();
    const hilo = hiloActual();
    // En una respuesta a una pregunta, las capturas son las del hilo.
    const capturasDelHilo = hilo.flatMap((tu) => (tu.rol === 'PERSONA' ? tu.capturas : []));
    const todas = [...capturasDelHilo, ...capturas];
    if ((!limpio && capturas.length === 0) || pensando) return;
    if (dictado.escuchando) dictado.parar();

    const historial: TurnoAsistente[] = hilo.flatMap((tu): TurnoAsistente[] => {
      if (tu.rol === 'PERSONA') return [{ rol: 'PERSONA', texto: tu.texto || '(capturas)' }];
      if (tu.rol === 'ASISTENTE') return [{ rol: 'ASISTENTE', texto: tu.propuesta.mensaje }];
      return [];
    });

    const persona: Turno = { id: nuevoId(), rol: 'PERSONA', texto: limpio, capturas };
    setTurnos((p) => [...p, persona]);
    setTexto('');
    setCapturas([]);
    setPensando(true);
    try {
      const { propuesta } = await api.crmAsistenteInterpretar({
        texto: limpio,
        imagenes: todas.map((c) => c.imagen),
        historial,
      });
      setTurnos((p) => [
        ...p,
        {
          id: nuevoId(),
          rol: 'ASISTENTE',
          propuesta,
          decisiones: propuesta.acciones.map(decisionInicial),
          estado: 'PENDIENTE',
        },
      ]);
    } catch (e) {
      const texto =
        e instanceof ErrorApi && e.mensajeClave === 'error.configuracion'
          ? t(idioma, 'crm.asistente.sinIa')
          : t(idioma, 'crm.asistente.error');
      setTurnos((p) => [...p, { id: nuevoId(), rol: 'AVISO', texto, error: true }]);
    } finally {
      setPensando(false);
    }
  }

  function decidir(turnoId: string, i: number, cambio: Partial<Decision>) {
    setTurnos((p) =>
      p.map((tu) =>
        tu.id === turnoId && tu.rol === 'ASISTENTE'
          ? { ...tu, decisiones: tu.decisiones.map((d, j) => (j === i ? { ...d, ...cambio } : d)) }
          : tu,
      ),
    );
  }

  async function aplicar(turno: Extract<Turno, { rol: 'ASISTENTE' }>) {
    const elegidas = turno.propuesta.acciones
      .map((a, i) => ({ a, d: turno.decisiones[i]! }))
      .filter(({ d }) => d.incluir)
      .map(({ a, d }) => ({
        oportunidadId: a.oportunidadId,
        nota: d.nota.trim() || null,
        nuevaEtapaId: d.mover ? a.nuevaEtapaId : null,
        tarea: d.tarea ? a.tarea : null,
      }))
      .filter((x) => x.nota || x.nuevaEtapaId || x.tarea);
    if (elegidas.length === 0) return;

    // La fuente de la nota es lo que escribió la persona en TODO el hilo: la
    // respuesta «la de Amber Village» sola no dice nada.
    const donde = turnos.indexOf(turno);
    const personas = turnos
      .slice(inicioDelHilo(turnos, donde), donde + 1)
      .filter((tu): tu is Extract<Turno, { rol: 'PERSONA' }> => tu.rol === 'PERSONA');

    setAplicando(true);
    try {
      const { oportunidades } = await api.crmAsistenteAplicar({
        origen: personas.map((p) => p.texto).filter(Boolean).join(' / '),
        capturas: personas.reduce((n, p) => n + p.capturas.length, 0),
        transcripcion: turno.propuesta.transcripcion,
        acciones: elegidas,
      });
      onAplicado(oportunidades);
      setTurnos((p) => [
        ...p.map((tu) => (tu.id === turno.id ? { ...tu, estado: 'APLICADA' as const } : tu)),
        {
          id: nuevoId(),
          rol: 'AVISO',
          texto:
            oportunidades.length === 1
              ? t(idioma, 'crm.asistente.aplicadoUna')
              : t(idioma, 'crm.asistente.aplicadoVarias', { n: oportunidades.length }),
        },
      ]);
    } catch (e) {
      setTurnos((p) => [
        ...p,
        {
          id: nuevoId(),
          rol: 'AVISO',
          texto: e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'),
          error: true,
        },
      ]);
    } finally {
      setAplicando(false);
    }
  }

  function descartar(turnoId: string) {
    setTurnos((p) => [
      ...p.map((tu) => (tu.id === turnoId ? { ...tu, estado: 'DESCARTADA' as const } : tu)),
      { id: nuevoId(), rol: 'AVISO', texto: t(idioma, 'crm.asistente.descartado') },
    ]);
  }

  const enviaConIntro = typeof window !== 'undefined' && !window.matchMedia?.('(pointer: coarse)').matches;

  return (
    <aside
      className={`asistente-crm${abierto ? ' abierto' : ''}`}
      aria-label={t(idioma, 'crm.asistente.titulo')}
      aria-hidden={!abierto}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        void anadirFicheros(e.dataTransfer.files);
      }}
    >
      <header className="asistente-cabeza">
        <h2>
          <Icono nombre="chispa" /> {t(idioma, 'crm.asistente.titulo')}
        </h2>
        <div className="fila-flex" style={{ gap: '0.25rem' }}>
          {turnos.length > 0 && (
            <button
              type="button"
              className="enlace"
              onClick={() => {
                setTurnos([]);
                setCapturas([]);
              }}
            >
              {t(idioma, 'crm.asistente.nuevo')}
            </button>
          )}
          <button
            type="button"
            className="modal-cerrar"
            aria-label={t(idioma, 'tarea.cerrar')}
            onClick={onCerrar}
          >
            <Icono nombre="cerrar" />
          </button>
        </div>
      </header>

      <div className="asistente-hilo" aria-live="polite">
        {turnos.length === 0 && <p className="asistente-intro">{t(idioma, 'crm.asistente.intro')}</p>}

        {turnos.map((tu) => {
          if (tu.rol === 'PERSONA') {
            return (
              <div key={tu.id} className="burbuja persona">
                {tu.capturas.length > 0 && (
                  <div className="miniaturas">
                    {tu.capturas.map((c) => (
                      <img key={c.id} src={c.url} alt="" />
                    ))}
                  </div>
                )}
                {tu.texto && <p>{tu.texto}</p>}
              </div>
            );
          }
          if (tu.rol === 'AVISO') {
            return (
              <p key={tu.id} className={`asistente-aviso${tu.error ? ' error' : ''}`} role={tu.error ? 'alert' : 'status'}>
                {tu.texto}
              </p>
            );
          }
          return (
            <Respuesta
              key={tu.id}
              idioma={idioma}
              turno={tu}
              ultimo={tu.id === turnos[turnos.length - 1]?.id}
              aplicando={aplicando}
              onDecidir={(i, c) => decidir(tu.id, i, c)}
              onAplicar={() => void aplicar(tu)}
              onDescartar={() => descartar(tu.id)}
              onResponder={(r) => void enviar(r)}
            />
          );
        })}

        {pensando && (
          <div className="burbuja asistente pensando" role="status">
            <p>{t(idioma, 'crm.asistente.pensando', { segundos })}</p>
            {segundos >= 8 && <p className="suave">{t(idioma, 'crm.asistente.pensandoPie')}</p>}
          </div>
        )}
        <div ref={fondo} />
      </div>

      <form
        className="asistente-escribir"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        {capturas.length > 0 && (
          <div className="miniaturas editables">
            {capturas.map((c) => (
              <span key={c.id} className="miniatura">
                <img src={c.url} alt="" />
                <button
                  type="button"
                  aria-label={t(idioma, 'crm.asistente.quitarCaptura')}
                  onClick={() => setCapturas((p) => p.filter((x) => x.id !== c.id))}
                >
                  <Icono nombre="cerrar" />
                </button>
              </span>
            ))}
          </div>
        )}
        {dictado.escuchando && (
          <p className="asistente-dictando" role="status">
            {t(idioma, 'crm.asistente.escuchando')}
          </p>
        )}
        {dictado.error && (
          <p className="asistente-dictando error" role="alert">
            {t(idioma, `crm.asistente.dictado.${dictado.error}`)}
          </p>
        )}
        <div className="asistente-fila">
          <button
            type="button"
            className="secundario icono-solo"
            aria-label={t(idioma, 'crm.asistente.adjuntar')}
            title={t(idioma, 'crm.asistente.adjuntar')}
            disabled={pensando || capturas.length >= MAX_CAPTURAS}
            onClick={() => fichero.current?.click()}
          >
            <Icono nombre="clip" />
          </button>
          {/*
            DICTAR. Se cuenta saliendo de una visita, con el móvil en la mano:
            dicho en tres segundos, tecleado en treinta. Lo dictado se escribe
            en el campo y no se envía solo, para corregir un nombre mal oído.
          */}
          <button
            type="button"
            className={`secundario icono-solo${dictado.escuchando ? ' escuchando' : ''}`}
            aria-pressed={dictado.escuchando}
            aria-label={t(idioma, dictado.escuchando ? 'crm.asistente.pararDictado' : 'crm.asistente.dictar')}
            title={t(idioma, dictado.escuchando ? 'crm.asistente.pararDictado' : 'crm.asistente.dictar')}
            disabled={pensando}
            onClick={() => (dictado.escuchando ? dictado.parar() : dictado.empezar(texto))}
          >
            <Icono nombre="microfono" />
          </button>
          <input
            ref={fichero}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              // Se capturan ANTES de vaciar el campo: la lista es viva y vaciarlo
              // la deja a cero.
              const elegidas = [...(e.target.files ?? [])];
              e.target.value = '';
              void anadirFicheros(elegidas);
            }}
          />
          <textarea
            ref={campo}
            rows={2}
            value={texto}
            placeholder={t(idioma, 'crm.asistente.placeholder')}
            aria-label={t(idioma, 'crm.asistente.placeholder')}
            onChange={(e) => setTexto(e.target.value)}
            onPaste={(e) => {
              // Pegar una captura con Ctrl+V es como más se comparte una
              // conversación desde el ordenador.
              const imagenes = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
              if (imagenes.length > 0) {
                e.preventDefault();
                void anadirFicheros(imagenes);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && enviaConIntro) {
                e.preventDefault();
                void enviar();
              }
            }}
          />
          <button
            type="submit"
            className="icono-solo"
            aria-label={t(idioma, 'crm.asistente.enviar')}
            title={t(idioma, 'crm.asistente.enviar')}
            disabled={pensando || (!texto.trim() && capturas.length === 0)}
          >
            <Icono nombre="enviar" />
          </button>
        </div>
      </form>
    </aside>
  );
}

/**
 * DÓNDE EMPIEZA EL HILO que llega hasta `hasta`.
 *
 * Una pregunta del asistente y la respuesta que se le da son el mismo hilo: al
 * contestar «la de Amber Village» hay que volver a mandarle lo de antes, que es
 * lo que da sentido a la respuesta. Una propuesta aplicada o descartada, o una
 * respuesta que no iba de clientes, lo cierra.
 */
function inicioDelHilo(lista: Turno[], hasta: number): number {
  let inicio = 0;
  for (let i = 0; i < hasta; i += 1) {
    const tu = lista[i]!;
    if (tu.rol === 'ASISTENTE' && (tu.estado !== 'PENDIENTE' || tu.propuesta.tipo === 'NADA')) {
      inicio = i + 1;
    }
  }
  return inicio;
}

// --- La respuesta del asistente ---------------------------------------------

function Respuesta({
  idioma,
  turno,
  ultimo,
  aplicando,
  onDecidir,
  onAplicar,
  onDescartar,
  onResponder,
}: {
  idioma: Idioma;
  turno: Extract<Turno, { rol: 'ASISTENTE' }>;
  ultimo: boolean;
  aplicando: boolean;
  onDecidir: (i: number, cambio: Partial<Decision>) => void;
  onAplicar: () => void;
  onDescartar: () => void;
  onResponder: (respuesta: string) => void;
}) {
  const { propuesta, decisiones, estado } = turno;
  const pendiente = estado === 'PENDIENTE';
  const elegidas = decisiones.filter((d) => d.incluir).length;

  return (
    <div className={`burbuja asistente${pendiente ? '' : ' cerrada'}`}>
      {propuesta.mensaje && <p>{propuesta.mensaje}</p>}

      {propuesta.tipo === 'PREGUNTA' && ultimo && propuesta.opciones.length > 0 && (
        <div className="asistente-opciones">
          {propuesta.opciones.map((o) => (
            <button key={o} type="button" className="secundario" onClick={() => onResponder(o)}>
              {o}
            </button>
          ))}
        </div>
      )}

      {propuesta.acciones.map((a, i) => (
        <Accion
          key={a.oportunidadId}
          idioma={idioma}
          accion={a}
          decision={decisiones[i]!}
          editable={pendiente}
          onCambio={(c) => onDecidir(i, c)}
        />
      ))}

      {propuesta.descartadas.length > 0 && (
        <div className="asistente-descartadas">
          <p className="suave">{t(idioma, 'crm.asistente.descartadas')}</p>
          <ul>
            {propuesta.descartadas.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      {propuesta.transcripcion && (
        <details className="asistente-transcripcion">
          <summary>{t(idioma, 'crm.asistente.transcripcion')}</summary>
          <p>{propuesta.transcripcion}</p>
        </details>
      )}

      {propuesta.acciones.length > 0 && pendiente && (
        <div className="asistente-botones">
          <button type="button" disabled={aplicando || elegidas === 0} onClick={onAplicar}>
            <Icono nombre="comprobado" />{' '}
            {elegidas > 1
              ? t(idioma, 'crm.asistente.aplicarN', { n: elegidas })
              : t(idioma, 'crm.asistente.aplicar')}
          </button>
          <button type="button" className="secundario" disabled={aplicando} onClick={onDescartar}>
            {t(idioma, 'crm.asistente.descartar')}
          </button>
        </div>
      )}
      {estado === 'APLICADA' && (
        <p className="asistente-estado verde">
          <Icono nombre="comprobado" /> {t(idioma, 'crm.asistente.aplicada')}
        </p>
      )}
    </div>
  );
}

function Accion({
  idioma,
  accion: a,
  decision: d,
  editable,
  onCambio,
}: {
  idioma: Idioma;
  accion: AccionPropuesta;
  decision: Decision;
  editable: boolean;
  onCambio: (cambio: Partial<Decision>) => void;
}) {
  return (
    <article className={`asistente-accion${d.incluir ? '' : ' apagada'}`}>
      <label className="asistente-accion-cabeza">
        {editable && (
          <input
            type="checkbox"
            checked={d.incluir}
            onChange={(e) => onCambio({ incluir: e.target.checked })}
          />
        )}
        <span className="crecer">
          <b>{a.oportunidadNombre}</b>
          <small className="suave">
            {a.embudoNombre} › {a.etapaActualNombre}
          </small>
        </span>
        <span className={`pastilla confianza-${a.confianza.toLowerCase()}`}>
          {t(idioma, `crm.asistente.confianza${a.confianza}`)}
        </span>
      </label>

      {a.porque && <p className="asistente-porque suave">{a.porque}</p>}

      {a.avisos.map((av) => (
        <p key={av} className="asistente-alerta">
          <Icono nombre="aviso" /> {av}
        </p>
      ))}

      {(a.nota !== null || editable) && (
        <div className="campo">
          <span className="asistente-etiqueta">{t(idioma, 'crm.asistente.nota')}</span>
          {editable ? (
            <textarea
              rows={Math.min(6, Math.max(2, Math.ceil(d.nota.length / 60)))}
              value={d.nota}
              disabled={!d.incluir}
              onChange={(e) => onCambio({ nota: e.target.value })}
            />
          ) : (
            <p>{d.nota}</p>
          )}
        </div>
      )}

      {a.nuevaEtapaNombre && (
        <label className="asistente-cambio">
          {editable && (
            <input
              type="checkbox"
              checked={d.mover}
              disabled={!d.incluir}
              onChange={(e) => onCambio({ mover: e.target.checked })}
            />
          )}
          <span>
            {t(idioma, 'crm.asistente.mover', { desde: a.etapaActualNombre, hasta: a.nuevaEtapaNombre })}
            {a.nuevaEtapaCierra && (
              <span className={`pastilla ${a.nuevaEtapaCierra === 'GANADA' ? 'VERDE' : 'ROJO'}`}>
                {t(idioma, a.nuevaEtapaCierra === 'GANADA' ? 'crm.asistente.ganada' : 'crm.asistente.perdida')}
              </span>
            )}
            {a.motivoEtapa && <small className="suave">{a.motivoEtapa}</small>}
          </span>
        </label>
      )}

      {a.tarea && (
        <label className="asistente-cambio">
          {editable && (
            <input
              type="checkbox"
              checked={d.tarea}
              disabled={!d.incluir}
              onChange={(e) => onCambio({ tarea: e.target.checked })}
            />
          )}
          <span>
            {a.tarea.vence
              ? t(idioma, 'crm.asistente.tareaVence', { titulo: a.tarea.titulo, fecha: a.tarea.vence })
              : t(idioma, 'crm.asistente.tarea', { titulo: a.tarea.titulo })}
          </span>
        </label>
      )}
    </article>
  );
}

/**
 * LO QUE SE MARCA SOLO, Y LO QUE NO.
 *
 * Lo dudoso empieza sin marcar: hay que querer aplicarlo. Y marcar una
 * oportunidad como GANADA nunca se hace solo, aunque el asistente esté seguro:
 * la regla es «solo con contrato», y eso lo confirma una persona.
 */
function decisionInicial(a: AccionPropuesta): Decision {
  return {
    incluir: a.confianza !== 'BAJA',
    nota: a.nota ?? '',
    mover: a.nuevaEtapaCierra !== 'GANADA',
    tarea: true,
  };
}

/** Reduce una captura a un tamaño razonable y la deja en base64. */
async function reducir(f: File): Promise<Captura> {
  const url = URL.createObjectURL(f);
  try {
    const img = await new Promise<HTMLImageElement>((ok, mal) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = mal;
      i.src = url;
    });
    const escala = Math.min(1, LADO_MAXIMO / Math.max(img.naturalWidth, img.naturalHeight));
    const ancho = Math.max(1, Math.round(img.naturalWidth * escala));
    const alto = Math.max(1, Math.round(img.naturalHeight * escala));
    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;
    const ctx = lienzo.getContext('2d');
    if (!ctx) throw new Error('sin lienzo');
    // Fondo blanco: una captura con transparencia se volvería negra en JPEG.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, ancho, alto);
    ctx.drawImage(img, 0, 0, ancho, alto);
    const datos = lienzo.toDataURL('image/jpeg', 0.9);
    return {
      id: nuevoId(),
      imagen: { tipoMime: 'image/jpeg', contenidoBase64: datos.slice(datos.indexOf(',') + 1) },
      url: datos,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
